/* eslint-disable @typescript-eslint/dot-notation */
import { IRepository } from '../../infrastructure/repository.interface';
import { Injectable } from '@angular/core';
import { ColumnModel, DataModel, PrimaryKeyValueModel, SurroundingIntervalsModel, YesNoEnum, ToastService, ToastDuration } from '@shared';
import { WorkspaceDatabaseProvider } from '../workspace-database.provider';
import { WorkspaceDatabaseKeys } from '../schemas';
import { combineLatest, firstValueFrom, Observable, of, map, catchError, switchMap, first } from 'rxjs';
import { MangoQuery, RxCollection, RxDocument } from 'rxdb';
import { ProjectCountAppModel } from '../../central-database/models/project-count.model';
import { RxdbIdProvider } from '../services';
import { LoggingService } from '@shared/logging';
import { maxBy, minBy } from 'lodash';
import { QueryBuilder } from './builders/query.builder';
import { QueryOperatorsEnum, QuerySortDirectionsEnum, RecordTypeEnum } from '../models';
import { SortQueryBuilder } from './builders';
import { addRxPlugin } from 'rxdb';
import { RxDBQueryBuilderPlugin } from 'rxdb/plugins/query-builder';
import { TableRepository } from './table.repository';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
addRxPlugin(RxDBQueryBuilderPlugin);

@Injectable()
export class DataRepository implements IRepository<DataModel> {
	// todo: split in more repositories, this one became too large
	// todo: [technical] add extra repository and copy the logic from each method, both this one and flat-table.data.repository.ts
	// here keep only a way to build an array of objects for db queries
	// ex: [{ [primaryKey]: { $eq: primaryKeyValue } }]
	public constructor(
		private dbProvider: WorkspaceDatabaseProvider,
		private rxdbIdProvider: RxdbIdProvider,
		private logger: LoggingService,
		private tableRepository: TableRepository,
		private toastService: ToastService,
		private translateService: TranslateService
	) {}

	public getObjectCount(sourceCollections: string[], projectNumber: string): Observable<ProjectCountAppModel> {
		const queries = [];
		// this goes to the database one time for each entity. If the number of projects increase on the page this should be changed
		for (const collection of sourceCollections) {
			queries.push(
				this.dbProvider
					.getDatabaseCollection(collection)
					.find()
					.where(WorkspaceDatabaseKeys.columns.projectNumber)
					.eq(projectNumber)
					.$.pipe(
						map((items: any[]) => {
							if (!items) {
								return 0;
							}

							return items.length;
						}),
						catchError((error) => of(error))
					)
			);
		}

		return combineLatest(queries).pipe(
			map(
				(counts: number[]) =>
					new ProjectCountAppModel(
						projectNumber,
						counts.reduce((accumulator, curr) => accumulator + curr)
					)
			)
		);
	}

public async insert(entity: DataModel, tableName: string, workfileId: string): Promise<void> {
    const collection = this.dbProvider.getDatabaseCollection(tableName.toLowerCase());
    let lastRxdbId = await this.rxdbIdProvider.getCurrentRxdbId(workfileId);

    entity.table_name = tableName;

    // This code was needed due to special case on handling isMaster only on drill hole child tables
    if (!(await firstValueFrom(this.tableRepository.isChildTableOfDrillHole$(tableName)))) {
        entity.is_master = YesNoEnum.Yes;
    }

    while (true) {
        entity.rxdbid = lastRxdbId + 1;
        entity.rxdbidstring = entity.rxdbid.toString();

        try {
            await collection.insert(entity);
            await this.rxdbIdProvider.setRxdbId(workfileId, entity.rxdbid);
            break;  // Exit loop if insert is successful
        } catch (error: any) {
            if (error.code === 'CONFLICT') {
                this.logger.warn('Insert conflict, retrying with incremented rxdbid', error);
                lastRxdbId++;
            } else {
                this.logger.error('Insert failed', error);
                throw error;
            }
        }
    }
}

	public async updateOne(
		tableData: Partial<DataModel>,
		tableName: string,
		id: number,
		primaryKeys: string[]
	): Promise<void> {
		const skippedFields = [WorkspaceDatabaseKeys.internalColumns.rxdbid, ...primaryKeys];

		const newTableData = Object.keys(tableData).reduce((cleansedTableData, key) => {
			if (!skippedFields.includes(key)) {
				cleansedTableData[key] = tableData[key];
			}
			return cleansedTableData;
		}, {});

		const collection = this.dbProvider.getDatabaseCollection(tableName.toLowerCase());
		const docs: RxDocument<DataModel>[] = await collection
			.find()
			.exec();
		docs.map(async (doc: RxDocument<DataModel>) => {
			if (doc.rxdbid == id) {
				await doc.patch(newTableData);
			}
		});
	}

	public async bulkInsert(entities: { [key: string]: DataModel[] }): Promise<void> {
		for (const key in entities) {
			if (entities.hasOwnProperty(key)) {
				const collection = this.dbProvider.getDatabaseCollection(key.toLowerCase());
				
				if (!collection) {
					await this.toastService.presentError(
						this.translateService.instant('importPage.messages.appendDifferentWorkfile'),
						ToastDuration.Slow
					);
					throw new Error('The collection you are trying to insert to was not found ' + key);
				}
	
				const tableIdsSet = new Set<string>(); // Track unique table IDs
				
				// eslint-disable-next-line @typescript-eslint/prefer-for-of
				for (let index = 0; index < entities[key].length; index++) {
					const element = entities[key][index];
					console.log(element);
					
					// Convert depth_from and depth_to to numbers if they exist
					if (
						element.hasOwnProperty(WorkspaceDatabaseKeys.columns.depthFrom) &&
						element.hasOwnProperty(WorkspaceDatabaseKeys.columns.depthTo)
					) {
						element.depth_from = Number(element.depth_from);
						element.depth_to = Number(element.depth_to);
					}
	
					if (!tableIdsSet.has(element.table_id)) {
						tableIdsSet.add(element.table_id);
					}
	
				}

				await collection.bulkInsert(entities[key].filter(el => tableIdsSet.has(el.table_id)));
			}
		}
	}

	public async delete(tableName: string, entryId: number): Promise<boolean> {
		const document = this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.find()
			.where(WorkspaceDatabaseKeys.internalColumns.rxdbid)
			.in([entryId])
			.limit(1)
			.remove();

		return (await document).length > 0 ? true : false;
	}

	public async fetchAllTableData(tableName: string) {
		try {
		  const documents = await lastValueFrom(this.getAllTableData$(tableName));
		  console.log(documents); // This will log the array of DataModel objects
		} catch (error) {
		  console.error('Error fetching data:', error);
		}
	}

	public getTableData$(tableName: string | null, projectNumber: string): Observable<DataModel[]> {
		// table DHL_BLAST_HOLE does not have a standard table associated
		if (!tableName) {
			return of([]);
		}

		const dbQuery = new QueryBuilder();
		dbQuery.addCondition(WorkspaceDatabaseKeys.columns.projectNumber, QueryOperatorsEnum.equal, projectNumber);
		dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.isDeleted, QueryOperatorsEnum.equal, false);

		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.find(dbQuery.build())
			.$.pipe(map((items: RxDocument<DataModel>[]) => items.map((item) => item.toJSON())));
	}

	public getAllTableData$(tableName: string): Observable<DataModel[]> {
		const collectionName = tableName.toLowerCase();
		return this.dbProvider
			.getDatabaseCollection(collectionName)
			.find()
			.$.pipe(map((items: RxDocument<DataModel>[]) => items.map((item: RxDocument<DataModel>) => item.toJSON())));
	}

	public getAllTableDataPromise(tableName: string): Promise<DataModel[]> {
		const collectionName = tableName.toLowerCase();

		return this.dbProvider.getDatabaseCollection(collectionName).find().exec();
	}

	public getChildTableData$(
		tableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[]
	): Observable<DataModel[]> {
		// todo: we return empty array when we try to combine data from samples and standards and standard table is not set
		// at entity level. Therefore, we only return sample data.
		if (!tableName) {
			return of([]);
		}

		const dbQuery = new QueryBuilder();
		dbQuery.addPrimaryKeys(primaryKeyValue);
		dbQuery.addCondition(WorkspaceDatabaseKeys.columns.projectNumber, QueryOperatorsEnum.equal, projectNumber);
		dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.isDeleted, QueryOperatorsEnum.equal, false);

		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.find(dbQuery.build())
			.$.pipe(map((items: RxDocument<DataModel>[]) => items.map((item: RxDocument<DataModel>) => item.toJSON())));
	}

	public hasAnyChildTableData$(
		tableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[],
		excludedId: number
	): Observable<boolean> {
		const dbQuery = new QueryBuilder();
		dbQuery.addPrimaryKeys(primaryKeyValue);
		dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.rxdbid, QueryOperatorsEnum.notEqual, excludedId);
		dbQuery.addCondition(WorkspaceDatabaseKeys.columns.projectNumber, QueryOperatorsEnum.equal, projectNumber);
		dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.isDeleted, QueryOperatorsEnum.equal, false);

		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.find(dbQuery.build())
			.limit(1)
			.$.pipe(map((anyData: DataModel[]) => (anyData.length > 0 ? true : false)));
	}

	public getOutsideIntervalData$(
		tableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[],
		excludedId: number,
		depthFrom: number,
		depthTo: number
	): Observable<DataModel[]> {
		const dbQuery: MangoQuery = {
			selector: {
				$and: [
					{ [WorkspaceDatabaseKeys.internalColumns.rxdbid]: { [QueryOperatorsEnum.notEqual]: excludedId } },
					{ [WorkspaceDatabaseKeys.columns.projectNumber]: { $eq: projectNumber } },
					{ [WorkspaceDatabaseKeys.internalColumns.isDeleted]: { $eq: false } }
				]
			}
		};

		primaryKeyValue.forEach((item: PrimaryKeyValueModel) => {
			dbQuery.selector.$and.push({ [item.primaryKey]: { $eq: item.primaryKeyValue } });
		});

		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.find(dbQuery)
			.$.pipe(
				map((items: RxDocument<DataModel>[]) => items.map((item) => item.toJSON())),
				// todo: this is a fix for RxDB $eq for numbers is not working on Android
				// $lte and $gte not working properly on Android
				// using multiple $in condition did not work
				map((items) => items.filter((x) => depthFrom >= Number(x.depth_from) && depthTo <= Number(x.depth_to)))
			);
	}

	public getInsideIntervalData$(
		tableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[],
		excludedId: number,
		depthFrom: number,
		depthTo: number
	): Observable<DataModel[]> {
		const dbQuery: MangoQuery = {
			selector: {
				$and: [
					{ [WorkspaceDatabaseKeys.internalColumns.rxdbid]: { [QueryOperatorsEnum.notEqual]: excludedId } },
					{ [WorkspaceDatabaseKeys.columns.projectNumber]: { $eq: projectNumber } },
					{ [WorkspaceDatabaseKeys.internalColumns.isDeleted]: { $eq: false } },
					{
						$or: [
							{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $gt: depthFrom } },
							{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $in: [depthFrom] } }
						]
					},
					{
						$or: [
							{ [WorkspaceDatabaseKeys.columns.depthTo]: { $lt: depthTo } },
							{ [WorkspaceDatabaseKeys.columns.depthTo]: { $in: [depthTo] } }
						]
					}
				]
			}
		};

		primaryKeyValue.forEach((item: PrimaryKeyValueModel) => {
			dbQuery.selector.$and.push({ [item.primaryKey]: { $eq: item.primaryKeyValue } });
		});

		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.find(dbQuery)
			.$.pipe(map((items: RxDocument<DataModel>[]) => items.map((item: RxDocument<DataModel>) => item.toJSON())));
	}

	public hasAnyStartsAtZeroData$(
		tableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[],
		excludedId: number
	): Observable<boolean> {
		const dbQuery = new QueryBuilder();
		dbQuery.addPrimaryKeys(primaryKeyValue);
		dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.rxdbid, QueryOperatorsEnum.notEqual, excludedId);
		dbQuery.addCondition(WorkspaceDatabaseKeys.columns.projectNumber, QueryOperatorsEnum.equal, projectNumber);
		dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.isDeleted, QueryOperatorsEnum.equal, false);
		dbQuery.addCondition(WorkspaceDatabaseKeys.columns.depthFrom, QueryOperatorsEnum.equal, 0);
		dbQuery.addCondition(WorkspaceDatabaseKeys.columns.depthTo, QueryOperatorsEnum.greaterThan, 0);

		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.find(dbQuery.build())
			.limit(1)
			.$.pipe(map((items: RxCollection<DataModel>[]) => items?.length !== 0));
	}

	public getOverlappingData$(
		tableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[],
		excludedId: number,
		depthFrom: number,
		depthTo: number
	): Observable<DataModel> {
		const dbQuery: MangoQuery = {
			selector: {
				$and: [
					{
						[WorkspaceDatabaseKeys.internalColumns.rxdbid]: { [QueryOperatorsEnum.notEqual]: excludedId },
						[WorkspaceDatabaseKeys.columns.projectNumber]: { $eq: projectNumber },
						[WorkspaceDatabaseKeys.internalColumns.isDeleted]: { $eq: false }
					},
					{
						$or: [
							{
								// overlap before
								$and: [
									{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $gt: depthFrom } },
									{ [WorkspaceDatabaseKeys.columns.depthTo]: { $gt: depthFrom } },
									{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $lt: depthTo } },
									{ [WorkspaceDatabaseKeys.columns.depthTo]: { $gte: depthTo } }
								]
							},
							{
								// overlap after
								$and: [
									{
										$or: [
											{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $lt: depthFrom } },
											{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $in: [depthFrom] } }
										]
									},
									{ [WorkspaceDatabaseKeys.columns.depthTo]: { $gt: depthFrom } },
									{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $lt: depthTo } },
									{ [WorkspaceDatabaseKeys.columns.depthTo]: { $lt: depthTo } }
								]
							},
							{
								// overlap wrap
								$and: [
									{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $gt: depthFrom } },
									{ [WorkspaceDatabaseKeys.columns.depthTo]: { $gt: depthFrom } },
									{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $lt: depthTo } },
									{ [WorkspaceDatabaseKeys.columns.depthTo]: { $lt: depthTo } }
								]
							},
							{
								// overlap inner before depthTo
								$and: [
									{
										$or: [
											{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $lt: depthFrom } },
											{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $in: [depthFrom] } }
										]
									},
									{ [WorkspaceDatabaseKeys.columns.depthTo]: { $gt: depthFrom } },
									{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $lt: depthTo } },
									{ [WorkspaceDatabaseKeys.columns.depthTo]: { $gt: depthTo } }
								]
							},
							{
								// overlap inner after depthFrom
								$and: [
									{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $lt: depthFrom } },
									{ [WorkspaceDatabaseKeys.columns.depthTo]: { $gt: depthFrom } },
									{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $lt: depthTo } },
									{ [WorkspaceDatabaseKeys.columns.depthTo]: { $gte: depthTo } }
								]
							}
						]
					}
				]
			}
		};

		dbQuery.selector.$and[0] = {
			...dbQuery.selector.$and[0],
			...primaryKeyValue.reduce((acc, item) => ((acc[item.primaryKey] = { $eq: item.primaryKeyValue }), acc), {})
		};

		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.findOne(dbQuery)
			.$.pipe(map((doc: RxDocument) => doc?.toJSON()));
	}

	public getDuplicateDataByDepths$(
		tableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[],
		excludedIds: number[],
		depthFrom: number,
		depthTo: number
	): Observable<boolean> {
		const dbQuery = new QueryBuilder();
		dbQuery.addPrimaryKeys(primaryKeyValue);
		// dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.rxdbid, QueryOperatorsEnum.notIn, excludedIds);

		excludedIds.forEach((x) => {
			dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.rxdbid, QueryOperatorsEnum.notEqual, x);
		});

		dbQuery.addCondition(WorkspaceDatabaseKeys.columns.projectNumber, QueryOperatorsEnum.equal, projectNumber);
		dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.isDeleted, QueryOperatorsEnum.equal, false);
		// todo: find why on Android is not working with $eq
		dbQuery.addCondition(WorkspaceDatabaseKeys.columns.depthFrom, QueryOperatorsEnum.include, [depthFrom]);
		dbQuery.addCondition(WorkspaceDatabaseKeys.columns.depthTo, QueryOperatorsEnum.include, [depthTo]);

		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.find(dbQuery.build())
			.limit(1)
			.$.pipe(map((items: RxDocument<DataModel>[]) => items?.length !== 0));
	}

	public getSurroundingIntervals$(
		tableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[],
		excludedId: number,
		depthFrom: number,
		depthTo: number
	): Observable<SurroundingIntervalsModel> {
		const dbQueryDepthFrom: MangoQuery = {
			selector: {
				$and: [
					{ [WorkspaceDatabaseKeys.internalColumns.rxdbid]: { [QueryOperatorsEnum.notEqual]: excludedId } },
					{ [WorkspaceDatabaseKeys.columns.projectNumber]: { $eq: projectNumber } },
					{ [WorkspaceDatabaseKeys.internalColumns.isDeleted]: { $eq: false } }
				]
			}
		};

		if (depthFrom) {
			dbQueryDepthFrom.selector.$and.push({
				$or: [
					{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $lt: depthFrom } },
					{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $in: [depthFrom] } }
				]
			});
		}

		const dbQueryDepthTo: MangoQuery = {
			selector: {
				$and: [
					{ [WorkspaceDatabaseKeys.internalColumns.rxdbid]: { [QueryOperatorsEnum.notEqual]: excludedId } },
					{ [WorkspaceDatabaseKeys.columns.projectNumber]: { $eq: projectNumber } },
					{ [WorkspaceDatabaseKeys.internalColumns.isDeleted]: { $eq: false } }
				]
			}
		};

		if (depthTo) {
			dbQueryDepthTo.selector.$and.push({
				$or: [
					{ [WorkspaceDatabaseKeys.columns.depthTo]: { $gt: depthTo } },
					{ [WorkspaceDatabaseKeys.columns.depthTo]: { $in: [depthTo] } }
				]
			});
		}

		primaryKeyValue.forEach((item: PrimaryKeyValueModel) => {
			dbQueryDepthFrom.selector.$and.push({ [item.primaryKey]: { $eq: item.primaryKeyValue } });
			dbQueryDepthTo.selector.$and.push({ [item.primaryKey]: { $eq: item.primaryKeyValue } });
		});

		return combineLatest([
			this.dbProvider
				.getDatabaseCollection(tableName.toLowerCase())
				.find(dbQueryDepthFrom)
				.$.pipe(
					map((items: RxDocument<DataModel>[]) => {
						const closestPrev = maxBy(items, 'depth_to');
						return closestPrev?.toJSON();
					})
				),
			this.dbProvider
				.getDatabaseCollection(tableName.toLowerCase())
				.find(dbQueryDepthTo)
				.$.pipe(
					map((items: RxDocument<DataModel>[]) => {
						const closestAfter = minBy(items, 'depth_from');
						return closestAfter?.toJSON();
					})
				)
		]).pipe(
			map((surroundingIntervals: [DataModel, DataModel]) => {
				const surrounding: SurroundingIntervalsModel = {
					previousInterval: surroundingIntervals[0],
					nextInterval: surroundingIntervals[1]
				};

				return surrounding;
			})
		);
	}

	public getTableEntry$(tableName: string, projectNumber: string, id: number): Observable<DataModel> {
		const dbQuery = new QueryBuilder();
		dbQuery.addCondition(WorkspaceDatabaseKeys.columns.projectNumber, QueryOperatorsEnum.equal, projectNumber);
		dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.isDeleted, QueryOperatorsEnum.equal, false);
		// todo: find why on Android is not working with $eq
		dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.rxdbid, QueryOperatorsEnum.include, [id]);

		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.findOne(dbQuery.build())
			.$.pipe(map((entry: RxDocument) => entry?.toJSON()));
	}

	public getTablePrimaryKey$(
		tableName: string,
		projectNumber: string,
		entryId: number,
		primaryKeys: string[]
	): Observable<PrimaryKeyValueModel[]> {
		const dbQuery = new QueryBuilder();
		dbQuery.addCondition(WorkspaceDatabaseKeys.columns.projectNumber, QueryOperatorsEnum.equal, projectNumber);
		dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.rxdbid, QueryOperatorsEnum.include, [entryId]);

		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.findOne(dbQuery.build())
			.$.pipe(
				map((item: RxDocument<DataModel>) =>
					item
						? Object.keys(item.toJSON())
								.filter((pk: string) => primaryKeys.includes(pk))
								.map((pk: string) => ({ primaryKey: pk, primaryKeyValue: item.toJSON()[pk] }))
						: primaryKeys.map((pk: string) => ({ primaryKey: pk, primaryKeyValue: null }))
				)
			);
	}

	public getColumnsMetadata$(tableName: string): Observable<ColumnModel[]> {
		return this.dbProvider
			.getDatabaseCollection(WorkspaceDatabaseKeys.columnsKey)
			.find()
			.where(WorkspaceDatabaseKeys.tables.tableName)
			.eq(tableName)
			.$.pipe(map((entries: RxDocument<ColumnModel>[]) => entries.map((item) => item.toJSON())));
	}

	public getDataLastId$(tableName: string): Observable<number> {
		const dbQuery = new QueryBuilder();
		// dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.rxdbid, QueryOperatorsEnum.notIn, [0]);
		dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.rxdbid, QueryOperatorsEnum.notEqual, 0);
		dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.isDeleted, QueryOperatorsEnum.equal, false);

		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.find(dbQuery.build())
			.sort({
				rxdbid: 'desc'
			})
			.limit(1)
			.$.pipe(
				switchMap((items: RxDocument<DataModel>[]) =>
					items.length !== 0
						? items.map((tableData: RxDocument<DataModel>) => (tableData ? tableData['rxdbid'] : 0))
						: of(0)
				)
			);
	}

	public getLastRecord$(
		tableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[],
		recordType: RecordTypeEnum
	): Observable<DataModel | null> {
		const dbQuery = new QueryBuilder();
		dbQuery.addPrimaryKeys(primaryKeyValue);
		// https://rxdb.info/rx-query.html#notice-rxdb-will-always-append-the-primary-key-to-the-sort-parameters
		dbQuery.addCondition('rxdbidstring', QueryOperatorsEnum.notEqual, '');
		// dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.rxdbid, QueryOperatorsEnum.notIn, [0]);
		dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.rxdbid, QueryOperatorsEnum.notEqual, 0);

		//todo: rxdb premium change
		// dbQuery.addConditionByRecordType(
		//   WorkspaceDatabaseKeys.columns.depthTo,
		//   QueryOperatorsEnum.notIn,
		//   [-1],
		//   recordType
		// );

		dbQuery.addConditionByRecordType(
			WorkspaceDatabaseKeys.columns.depthTo,
			QueryOperatorsEnum.notEqual,
			-1,
			recordType
		);

		dbQuery.addCondition(WorkspaceDatabaseKeys.columns.projectNumber, QueryOperatorsEnum.equal, projectNumber);
		dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.isDeleted, QueryOperatorsEnum.equal, false);

		const dbSortQuery = new SortQueryBuilder();
		dbSortQuery.addColumnAndDirectionByRecordType(
			WorkspaceDatabaseKeys.columns.depthTo,
			QuerySortDirectionsEnum.desc,
			recordType
		);

		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.find(dbQuery.build())
			.sort(dbSortQuery.build())
			.limit(1)
			.$.pipe(
				switchMap((items: RxDocument<DataModel>[]) =>
					items.length !== 0 ? items.map((tableData: RxDocument<DataModel>) => tableData) : of(null)
				)
			);
	}

	public isTablePrimaryKeyValueUnique$(
		tableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[],
		excludedId: number,
		includeSoftDeleted: boolean
	): Observable<boolean> {
		const dbQuery = new QueryBuilder();
		// this is a hack, basically rxdb caches the result sometimes and if you run the query
		// with the same parameters its returns the same result
		const randvalue = -Math.floor(Math.random() * 100000000);

		// dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.rxdbid, QueryOperatorsEnum.notIn, [
		//   excludedId,
		//   randvalue
		// ]);
		dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.rxdbid, QueryOperatorsEnum.notEqual, excludedId);
		dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.rxdbid, QueryOperatorsEnum.notEqual, randvalue);

		dbQuery.addCondition(WorkspaceDatabaseKeys.columns.projectNumber, QueryOperatorsEnum.equal, projectNumber);
		dbQuery.addCondition(
			WorkspaceDatabaseKeys.internalColumns.isDeleted,
			QueryOperatorsEnum.equal,
			includeSoftDeleted
		);

		primaryKeyValue.forEach((keyValue: PrimaryKeyValueModel) => {
			if (keyValue.primaryKeyValue) {
				dbQuery.addCondition(keyValue.primaryKey, QueryOperatorsEnum.equal, keyValue.primaryKeyValue);
			}
		});

		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.find(dbQuery.build())
			.limit(1)
			.$.pipe(map((item: RxDocument<DataModel>[]) => item.length === 0));
	}

	public isTablePropertyValueUnique$(
		tableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[],
		property: string,
		propertyValue: string,
		excludedId: number,
		includeSoftDeleted: boolean
	): Observable<boolean> {
		if (!tableName) {
			return of(true);
		}

		const dbQuery = new QueryBuilder();
		dbQuery.addPrimaryKeys(primaryKeyValue);
		dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.rxdbid, QueryOperatorsEnum.notEqual, excludedId);
		dbQuery.addCondition(WorkspaceDatabaseKeys.columns.projectNumber, QueryOperatorsEnum.equal, projectNumber);
		dbQuery.addCondition(
			WorkspaceDatabaseKeys.internalColumns.isDeleted,
			QueryOperatorsEnum.equal,
			includeSoftDeleted
		);

		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.find(dbQuery.build())
			.$.pipe(
				first(),
				map((item: RxDocument<DataModel>[]) => {
					const filteredItem = item.filter(
						(document: RxDocument<DataModel>) =>
							document.toJSON()[property].toLowerCase() === propertyValue.toLowerCase()
					);
					return filteredItem.length === 0;
				})
			);
	}

	public getLastPropertyValue$(
		tableName: string | null,
		property: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[]
	): Observable<string | number | null> {
		if (!tableName) {
			return of(null);
		}

		const dbQuery = new QueryBuilder();
		dbQuery.addPrimaryKeys(primaryKeyValue);
		// https://rxdb.info/rx-query.html#notice-rxdb-will-always-append-the-primary-key-to-the-sort-parameters
		dbQuery.addCondition('rxdbidstring', QueryOperatorsEnum.notEqual, '');
		//todo: rxdb upgrade change
		// dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.rxdbid, QueryOperatorsEnum.notIn, [0]);
		dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.rxdbid, QueryOperatorsEnum.notEqual, 0);
		dbQuery.addCondition(WorkspaceDatabaseKeys.columns.projectNumber, QueryOperatorsEnum.equal, projectNumber);

		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.find(dbQuery.build())
			.sort({
				rxdbid: 'desc'
			})
			.limit(1)
			.$.pipe(
				switchMap((items: RxDocument<DataModel>[]) =>
					items.length !== 0
						? items.map((tableData: RxDocument<DataModel>) =>
								tableData && tableData[property] ? tableData[property] : null
						  )
						: of(null)
				)
			);
	}

	public getTablePropertyValueById$(
		tableName: string,
		id: number,
		propertyName: string
	): Observable<string | number | boolean | null> {
		return this.getTableDataById$(tableName, id).pipe(
			map((item: RxDocument<DataModel>) => (item ? item[propertyName] : null))
		);
	}

	public getTableDataById$(tableName: string, id: number): Observable<DataModel | null> {
		// todo: find why on Android is not working with eq
		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.findOne()
			.where('rxdbid')
			.in([id])
			.$.pipe(map((item: RxDocument<DataModel>) => (item ? item : null)));
	}

	public getRecordsTotalCount$(
		sourceCollections: string[],
		columnKeyValue: { [key: string]: string } | any
	): Observable<any> {
		const queries = [];
		for (const collection of sourceCollections) {
			const dbQuery = new QueryBuilder();
			dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.isDeleted, QueryOperatorsEnum.equal, false);

			for (const [k, v] of Object.entries(columnKeyValue)) {
				if (k && v) {
					dbQuery.addCondition(k, QueryOperatorsEnum.equal, v);
				}
			}

			queries.push(
				this.dbProvider
					.getDatabaseCollection(collection)
					.find(dbQuery.build())
					.$.pipe(
						map((items: RxDocument<DataModel>[]) => items.length),
						catchError((error) => of(error))
					)
			);
		}

		return combineLatest(queries).pipe(
			map((recordsCount: number[]) => recordsCount.reduce((acc: number, curr: number) => acc + curr, 0))
		);
	}

	public getIsMaster$(tableName: string, projectNumber: string, id: number): Observable<DataModel> {
		return this.getTableEntry$(tableName, projectNumber, id);
	}

	public async deleteRecords(
		tableName: string,
		projectNumber: string,
		primaryKeyValue: PrimaryKeyValueModel[]
	): Promise<DataModel[]> {
		const dbQuery = new QueryBuilder();
		dbQuery.addPrimaryKeys(primaryKeyValue);
		dbQuery.addCondition(WorkspaceDatabaseKeys.columns.projectNumber, QueryOperatorsEnum.equal, projectNumber);
		dbQuery.addCondition(WorkspaceDatabaseKeys.internalColumns.isDeleted, QueryOperatorsEnum.equal, false);
		return this.dbProvider.getDatabaseCollection(tableName.toLowerCase()).find(dbQuery.build()).remove();
	}
}
